import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import UnauthorizedLayout from '../UnauthorizedLayout'
import AuthService from '../../../services/AuthService'
import FormInput from '../../ui/FormInput'
import FormButton from '../../ui/FormButton'
import useNotification from '../../../hooks/useNotification'

const ConfirmPassword = () => {
  const { t } = useTranslation('auth')
  const effectRan = useRef(false)
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [loading, setLoading] = useState(true)
  const [email, setEmail] = useState('')

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  const { info, error: showError } = useNotification()
  const navigate = useNavigate()

  const onSubmit = async (data) => {
    try {
      const response = await AuthService.confirmPassword(token, data.password)
      info(response.data.message || t('confirmPassword.toast.success'))
      navigate('/login')
    } catch (e) {
      console.error('Confirm password failed:', e?.response?.data)
      const errorMessage =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        t('confirmPassword.toast.error')
      showError(errorMessage)
      reset()
    }
  }

  const backToLogin = () => {
    navigate('/login')
  }

  useEffect(() => {
    if (effectRan.current === false && token) {
      AuthService.verifyPasswordToken(token)
        .then((response) => {
          if (response.data.valid) {
            setEmail(response.data.email || '')
          } else {
            showError(
              response.data.error || t('confirmPassword.toast.invalidLink')
            )
            navigate('/login')
          }
        })
        .catch((e) => {
          console.error(
            'Password confirmation link is not valid:',
            e?.response?.data
          )
          const errorMessage =
            e?.response?.data?.error ||
            e?.response?.data?.message ||
            t('confirmPassword.toast.invalidLink')
          showError(errorMessage)
          navigate('/login')
        })
        .finally(() => setLoading(false))
      return () => {
        effectRan.current = true
      }
    } else if (!token) {
      showError(t('confirmPassword.toast.invalidLink'))
      navigate('/login')
      setLoading(false)
    }
  }, [token, navigate, t, showError])

  if (loading) return <></>

  return (
    <UnauthorizedLayout
      title={t('confirmPassword.title')}
      subtitle={
        email
          ? t('confirmPassword.subtitleWithEmail', { email })
          : t('confirmPassword.subtitle')
      }
      question={t('confirmPassword.question')}
      text={t('confirmPassword.backLink')}
      hancleClick={backToLogin}
    >
      <div className="w-[100%] desktop:w-[500px] flex flex-col gap-4">
        <form
          className="mt-6 flex flex-col gap-2"
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormInput
            label={t('labels.password')}
            name="password"
            placeholder={t('confirmPassword.placeholder.password')}
            register={register}
            validationRules={{
              required: t('validation.passwordRequired'),
              minLength: {
                value: 6,
                message: t('validation.passwordMinLength'),
              },
            }}
            errors={errors}
            withPasswordToggle={true}
          />
          <FormInput
            label={t('labels.confirmPassword')}
            name="confirmPassword"
            type="password"
            placeholder={t('confirmPassword.placeholder.confirmPassword')}
            register={register}
            validationRules={{
              required: t('validation.confirmPasswordRequired'),
              validate: (value) =>
                value === getValues('password') ||
                t('validation.passwordsNotMatch'),
            }}
            errors={errors}
            withPasswordToggle={true}
          />
          <div className="mt-3">
            <FormButton
              isSubmitting={isSubmitting}
              text={t('confirmPassword.button')}
            />
          </div>
        </form>
      </div>
    </UnauthorizedLayout>
  )
}

export default ConfirmPassword
